import React from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { textCenter, doughnutLegendPlugin } from '/src/utilities/chart-plugins';
import { formatPercent, colors } from '/src/utilities/formats';

ChartJS.register(ArcElement, Legend, Tooltip);

const DoughnutChart = ({
    data = null,
    datasets = null,
    secondaryData = null,
    centerNumber = false,
    centerText = false,
    legendDiv = false,
    numberFormat = null,
    colorRamp = null
}) => {

    if (!data && !datasets) return null;


    // create an object mapping the labels to the values for doughnutLegendPlugin
    dataValueMap = {};
    if (data) {
        data.forEach(dataPoint => {
            dataValueMap[dataPoint.label] = formatPercent(dataPoint.value, 1);
        });
    }
    if (datasets) {
        datasets.datasets[0].data.forEach((dataPoint, index) => {
            dataValueMap[datasets.labels[index]] = formatPercent(dataPoint, 1);
        });
    }

    // specify a default color ramp if one is not passed in. 
    if (!colorRamp) {
        colorRamp = [
            colors.lightblue, // light blue
            colors.red, // red
            colors.green, // green
            colors.orange, // orange
            colors.darkred, // dark red
            colors.darkblue, // dark blue
            colors.pink, // pink
        ]
    }

    const options = {
        hover: { mode: null },
        animation: false,
        responsive: true,
        maintainAspectRatio: false,
        cutout: '75%',
        plugins: {
            textCenter: {
                centerNumber: centerNumber,
                centerText: centerText
            },
            doughnutLegend: {
                // ID of the container to put the legend in
                containerID: legendDiv,
                labelsToValues: dataValueMap,
            },
            legend: {
                display: false,
                position: 'right',
                maxWidth: 250,
                itemWrap: true,
                labels: {
                    font: {
                        family: '"Open Sans", sans-serif',
                        fontSize: 14,
                    },
                    color: '#000',
                    boxWidth: 10,
                    boxHeight: 10,
                    usePointStyle: true,
                    generateLabelsx: (chart) => {
                        const data = chart.data;
                        if (data.labels.length && data.datasets.length) {
                            const { labels: { pointStyle, color } } = chart.legend.options;

                            return data.labels.map((label, i) => {
                                const meta = chart.getDatasetMeta(0);
                                const style = meta.controller.getStyle(i);

                                return {
                                    text: `${label} (${data.datasets[0].data[i].value.toLocaleString()})%`,
                                    fillStyle: style.backgroundColor,
                                    strokeStyle: style.borderColor,
                                    fontColor: color,
                                    lineWidth: style.borderWidth,
                                    pointStyle: pointStyle,
                                    hidden: !chart.getDataVisibility(i),

                                    // Extra data used for toggling the correct item
                                    index: i
                                };
                            });
                        }
                        return [];
                    }
                },
            }, // end legend
            // format tooltip numbers
            tooltip: {
                callbacks: {
                    label: function (tooltipItem) {
                        // check if tooltipItem is defined and has a raw value
                        // formatting may already have been done in parent component
                        if (typeof (tooltipItem) != 'undefined' && typeof (tooltipItem.raw) != 'undefined') {

                            let floatValue = 0;
                            // get the float value from the tooltipItem
                            if (typeof (tooltipItem.raw) == 'number') {
                                floatValue = tooltipItem.raw;
                            } else if (typeof (tooltipItem.raw.value) == 'number') {
                                floatValue = tooltipItem.raw.value;
                            } else {
                                floatValue = parseFloat(tooltipItem.raw.value);
                            }

                            let decimalPlaces = 1;
                            let format = null;
                            // if numberFormat is an array use the first element as the format and the second as the decimal places
                            if (Array.isArray(numberFormat)) {
                                format = numberFormat[0];
                                decimalPlaces = numberFormat[1];
                            } else {
                                format = numberFormat;
                                decimalPlaces = 1;
                            }

                            let strValue = floatValue.toLocaleString();
                            switch (format) {
                                case 'currency':
                                    return ` ${strValue} USD`;
                                case 'percent':
                                    if (floatValue == 0 || floatValue == 100) {
                                        return ` ${floatValue}%`;
                                    }
                                    return ` ${floatValue.toFixed(decimalPlaces)}%`;
                                default:
                                    return ` ${strValue}`;
                            }
                        }
                    },
                },
            },

        },
    }

    let formattedData;
    if (datasets) {
        formattedData = datasets;
    }
    else {
        formattedData = {
            labels: data.map(dataPoint => dataPoint.label),
            datasets: [{
                data: data,
                radius: '95%',
                weight: 1,
                backgroundColor: colorRamp,
                borderColor: [
                    '#FFFFFF',
                    '#FFFFFF',
                    '#FFFFFF',
                    '#FFFFFF',
                    '#FFFFFF',
                    '#FFFFFF',
                ],
                borderWidth: 2,
            }],
        };
    }
    return (
        <div>
            <div className="doughnut-chart" style={{ height: "300px" }}>
                <Doughnut data={formattedData} options={options} plugins={[textCenter, doughnutLegendPlugin]} />
            </div>
        </div>
    )
        ;
};


export default DoughnutChart;

